import moment from 'moment';
import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import Util from '../../../../util/util';
import {createFragmentContainer, graphql} from 'react-relay';
import {
	Modal,
	Text,
	Label,
	TextArea,
	FlexColumn,
	DateRangePicker,
	ButtonDropdownField,
	DropdownList,
	ComboBox,
	FlexRow,
	Avatar,
} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {getLockedDate} from '../../../../../../components/timesheets/time-lock/TimeLockUtil';
import {TRACKING_OBJECTS} from '../../../../../../tracking/amplitude/constants/TrackingObjects';
import {trackEvent} from '../../../../../../tracking/amplitude/TrackingV2';
import {convertIntoFloatHoursFormatWithGranularity} from '../../../inputs/hours-input/hours_input_logic';
import {TimeOffModalCreate_viewer} from './__generated__/TimeOffModalCreate_viewer.graphql';
import {createToast} from '../../../toasts/another-toast/toaster';
import {cloneDeep} from 'lodash';
import {HoursInput} from '../../../inputs/hours-input/hours_input_view';
import {workingHourForTheDay} from '../../../../../my-work-tab/my-timesheets-page/top-section/TimesheetsCalendarUtil';
import CreateTimeRegistrationMutation from '../../../../../../mutations/create_time_registration_mutation.modern';
import CreateMultipleTimeRegistrationMutation from '../../../../../../mutations/create_multiple_time_registration_mutation.modern';
import {isTimeRegistrationNoteRequired} from '../../../../util/cache/TimeRegistrationSettingsUtil';
import {isTimeRegistrationNoteInvalid} from '../../../../util/time-registration/time-registration-settings/TimeRegistrationNoteFilter';
import {Person} from '../../../../../my-work-tab/my-timesheets-page/top-section/TimesheetsCalendarTypes';
import {hasPermission} from '../../../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../../Permissions';
import {profilePicRectSrc} from '../../../../../../directApi';

type IdleTimeType = NonNullable<
	NonNullable<NonNullable<NonNullable<NonNullable<TimeOffModalCreate_viewer['company']>['idleTimes']>['edges']>[0]>['node']
>;

type IdleTimeEdgeType = {
	node: IdleTimeType;
};
type DatePickerDate = string | null;

type DatePickerOnChangeEvent = {
	startDate: DatePickerDate;
	endDate: DatePickerDate;
};

type PersonDropdownOption = {
	id: string;
	name: string;
	profilePictureId: string;
};

const getIdleTimeOffOptions = (viewer: TimeOffModalCreate_viewer) => {
	const idleTimes = viewer?.company?.idleTimes?.edges;
	if (!idleTimes || !Array.isArray(idleTimes)) return [];

	const sortedIdleTimes = cloneDeep(idleTimes).sort((a, b) => Util.sortAlphabetically(a.node.name, b.node.name));

	return sortedIdleTimes
		.filter((idleTime: IdleTimeEdgeType) => !idleTime.node.disabled && !idleTime.node.isInternalTime)
		.map((idleTime: IdleTimeEdgeType) => ({
			cy: `option-${idleTime.node.name}`,
			id: idleTime.node.id,
			name: idleTime.node.name,
		}));
};

export type TimeOffModalCreateProps = {
	viewer: TimeOffModalCreate_viewer;
	closeModal: () => void;
};

function TimeOffModalCreate({viewer, closeModal}: TimeOffModalCreateProps): JSX.Element {
	const today = moment();
	const {formatMessage} = useIntl();

	const [canAddTimeOff, setCanAddTimeOff] = useState<boolean>(false);
	const [isSubmittingTimeOff, setIsSubmittingTimeOff] = useState<boolean>(false);
	const [selectedPerson, setSelectedPerson] = useState(viewer.actualPersonId);

	const lockedDate = useMemo(() => {
		const person = viewer.company?.allPersons?.edges?.find(edge => edge?.node?.id === selectedPerson)?.node;
		return getLockedDate(viewer.company, person);
	}, [selectedPerson]);

	const noteRequired = isTimeRegistrationNoteRequired();

	const timeOffOptions = useMemo(() => getIdleTimeOffOptions(viewer), [viewer]);
	const [selectedIdleTime, setSelectedIdleTime] = useState<IdleTimeType | undefined>(undefined);

	const [inputValue, setInputValue] = useState('');
	const [timeInputValue, setTimeInputValue] = useState(0);

	const [selectedStartDate, setSelectedStartDate] = useState<string | null>(
		lockedDate && lockedDate.isSameOrAfter(today, 'day')
			? lockedDate.clone().add(1, 'd').format('YYYY-MM-DD')
			: today.format('YYYY-MM-DD')
	);

	const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);

	const [note, setNote] = useState('');
	const [noteError, setNoteError] = useState(false);

	const setSuggestedValueToInput = value => {
		setTimeInputValue(Util.convertTimeInputToMinutes(value != null ? `${value}h` : undefined, true));
	};

	const dateRangeChange = (dates: DatePickerOnChangeEvent) => {
		setSelectedStartDate(dates.startDate);
		setSelectedEndDate(dates.endDate);
	};

	const workingHoursForDay = useMemo(
		() =>
			workingHourForTheDay(
				{...viewer, id: viewer.actualPersonId} as unknown as Person,
				selectedStartDate ? moment(selectedStartDate).toDate() : new Date()
			),
		[selectedStartDate, viewer]
	);

	const personDropdownOptions = useMemo(() => {
		const personDropdownOptions = Util.getPersonsForDropdown(viewer?.company?.allPersons);
		return personDropdownOptions.map(personDropdownOption => ({
			id: personDropdownOption.value,
			name: personDropdownOption.label,
			...personDropdownOption,
		})) as PersonDropdownOption[];
	}, [viewer?.company?.allPersons]);

	const isTimeOffValid = () => {
		if (!selectedIdleTime) {
			return false;
		}

		if (typeof timeInputValue !== 'number' || isNaN(timeInputValue)) {
			return false;
		}

		if (!selectedStartDate) {
			return false;
		}

		if (lockedDate && lockedDate.isAfter(selectedStartDate, 'day')) {
			return false;
		}

		if (selectedEndDate && moment(selectedStartDate).isAfter(selectedEndDate, 'day')) {
			return false;
		}

		return true;
	};

	useEffect(() => {
		setCanAddTimeOff(isTimeOffValid());
	}, [selectedIdleTime, timeInputValue, selectedStartDate, selectedEndDate]);

	const handleChangeTimeOffDropdown = useCallback(
		(timeOffOptionId: string) => {
			const idleTimeNode =
				viewer.company?.idleTimes?.edges?.find(edge => edge?.node?.id === timeOffOptionId)?.node || undefined;

			const trackEventProperties = idleTimeNode
				? {
						id: idleTimeNode.id,
						name: idleTimeNode.name,
				  }
				: {};

			trackEvent('Time Off Modal Idle Time', 'Selected', trackEventProperties);
			setSelectedIdleTime(idleTimeNode);
		},
		[viewer]
	);

	const onNoteChange = (value: string) => {
		setNoteError(false);
		setNote(value);
	};

	const registerTime = () => {
		setIsSubmittingTimeOff(true);
		const minutesRegistered = timeInputValue || 0;

		// If nothing is selected, set a single date to today
		const startDate = selectedStartDate ? moment(selectedStartDate) : today;
		const endDate = selectedEndDate ? moment(selectedEndDate) : today;

		const noteInvalid = isTimeRegistrationNoteInvalid(note);
		if (noteRequired && noteInvalid) {
			setNoteError(true);
			setIsSubmittingTimeOff(false);
			return;
		}

		trackEvent('Time Off Modal Add Button', 'Selected', {
			idleTimeId: selectedIdleTime?.id,
			minutesRegistered: minutesRegistered,
			startDate: startDate.format('YYYY-MM-DD'),
			endDate: endDate.format('YYYY-MM-DD'),
			notes: note,
		});

		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: formatMessage({id: 'timer_modal.registered_toast_text'}),
			});

			setIsSubmittingTimeOff(false);
			closeModal();
		};

		const mutationPayload = {
			viewer: viewer,
			source: 'Time-Off-Modal-Create',
			idleTimeId: selectedIdleTime?.id,
			minutesRegistered: minutesRegistered,
			day: startDate.date(),
			month: startDate.month() + 1,
			year: startDate.year(),
			personId: selectedPerson,
			notes: note || null,
		};

		if (selectedEndDate) {
			Util.CommitMutation(
				CreateMultipleTimeRegistrationMutation,
				{
					...mutationPayload,
					endDay: endDate.date(),
					endMonth: endDate.month() + 1,
					endYear: endDate.year(),
				},
				onSuccess
			);
		} else {
			Util.CommitSchedulingModalUpdate(CreateTimeRegistrationMutation, mutationPayload, onSuccess);
		}
	};

	const dummyRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		//Fix buggy Radix button focus on open modal
		const timeout = setTimeout(() => {
			dummyRef.current?.focus();
		}, 0);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="modal.time_off.title" />
			</Modal.Title>
			<Modal.Content>
				<div ref={dummyRef} tabIndex={-1} />
				<FlexColumn gap={'l'}>
					{hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL) ? (
						<FlexColumn>
							<Label>
								<FormattedMessage id="common.person" />
							</Label>
							<ComboBox
								defaultItems={personDropdownOptions}
								width="flex"
								defaultSelectedKey={selectedPerson || undefined}
								onChange={setSelectedPerson}
								aria-label="Person Selector"
								data-cy={'time-off-select-person'}
							>
								{person => (
									<ComboBox.Item textValue={person.name}>
										<FlexRow>
											<Avatar
												name={person.name}
												src={
													person.profilePictureId ? profilePicRectSrc(person.profilePictureId) : null
												}
												size="s"
											/>
											<Text>{person.name}</Text>
										</FlexRow>
									</ComboBox.Item>
								)}
							</ComboBox>
						</FlexColumn>
					) : (
						<></>
					)}
					<FlexColumn>
						<Label>
							<FormattedMessage id="modal.time_off.time_off_dropdown.label" />
						</Label>
						<DropdownList defaultOpen>
							<DropdownList.Trigger data-cy="time-off-modal-select-time-off">
								<ButtonDropdownField>
									{selectedIdleTime ? (
										<Text>{selectedIdleTime.name}</Text>
									) : (
										<Text color="auxiliary">
											<FormattedMessage id="overview_time.select_time-off" />
										</Text>
									)}
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content offset={10}>
								{timeOffOptions.map(timeOffOption => (
									<DropdownList.Item
										key={timeOffOption.id}
										data-cy={timeOffOption.cy}
										onClick={() => handleChangeTimeOffDropdown(timeOffOption.id)}
									>
										{timeOffOption.name}
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</FlexColumn>

					<FlexColumn>
						<Label>
							<FormattedMessage id="modal.time_off.time_entry.label" />
						</Label>
						<HoursInput
							placeholder={formatMessage({id: 'hours_input.placeholder'})}
							customClassName={'hour-input'}
							cy={'new-billable-time-entry-input'}
							mutation={(value: string) => setInputValue(value)}
							value={inputValue}
							replicateDesignSystem={true}
							width={'184px'}
							onInputFormatted={value => {
								if (value !== null) {
									setSuggestedValueToInput(value);
								}
							}}
							onlyFormatGranularityOnBlur={true}
							granularityFormatter={(val, intl, minuteLimit, showAsterisk) =>
								convertIntoFloatHoursFormatWithGranularity(
									val,
									intl,
									minuteLimit,
									showAsterisk,
									workingHoursForDay
								)
							}
							isTimeregInput
						/>
					</FlexColumn>

					<FlexColumn>
						<Label>
							<FormattedMessage id="common.date" />
						</Label>
						<DateRangePicker
							aria-label="Select date range"
							data-cy={'time-off-modal-date-range-picker'}
							startDate={selectedStartDate}
							endDate={selectedEndDate}
							onChange={(dates: DatePickerOnChangeEvent) => dateRangeChange(dates)}
							width={'flex'}
							minValue={lockedDate ? lockedDate.clone().add(1, 'd').format('YYYY-MM-DD') : undefined}
						/>
					</FlexColumn>

					<FlexColumn>
						<TextArea
							label={formatMessage({id: 'common.notes'})}
							height={80}
							value={note}
							isOptional={!noteRequired}
							placeholder={formatMessage({id: 'common.enter_notes'})}
							aria-label={'Enter notes'}
							data-cy={'time-off-modal-notes'}
							onChange={onNoteChange}
							onBlur={() => trackEvent(TRACKING_OBJECTS.TIME_REGISTRATION_MODAL, 'Note Input Updated')}
							errorText={
								noteError
									? 'Adding a note is required for time entries. This is a company-wide setting.'
									: undefined
							}
							maxLength={999}
						/>
					</FlexColumn>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={registerTime}
					disabled={!canAddTimeOff || isSubmittingTimeOff}
					dataCy="time-off-modal-add-button"
				>
					<FormattedMessage id="common.add" />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton
					onPress={closeModal}
					disabled={isSubmittingTimeOff}
					dataCy="time-off-modal-cancel-button"
				>
					<FormattedMessage id={'common.cancel'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}

export const TimeOffModalCreateQuery = graphql`
	query TimeOffModalCreate_Query($personId: ID) {
		viewer {
			actualPersonId
			component(name: "time_off_modal_create")
			...TimeOffModalCreate_viewer @arguments(personId: $personId)
		}
	}
`;

export default createFragmentContainer(TimeOffModalCreate, {
	viewer: graphql`
		fragment TimeOffModalCreate_viewer on Viewer @argumentDefinitions(personId: {type: "ID!"}) {
			id
			harvestUser
			language
			actualPersonId
			timerStartDate
			timerEndDate
			excludeFromCompanyLockedPeriod
			submitLockedDateYear
			submitLockedDateMonth
			submitLockedDateDay
			startDate
			endDate
			createdAt
			monday
			tuesday
			wednesday
			thursday
			friday
			saturday
			sunday
			availableFeatureFlags {
				key
			}
			company {
				id
				bambooHREnabled
				hiBobEnabled
				harvestEnabled
				characterLimit
				lockedPeriodYear
				lockedPeriodMonth
				lockedPeriodDay
				roles(includeDisabled: false) {
					edges {
						node {
							id
							name
						}
					}
				}
				modules {
					moduleType
				}
				idleTimes(first: 10000) {
					edges {
						node {
							id
							name
							isInternalTime
							disabled
						}
					}
				}
				person(id: $personId) {
					id
					role {
						id
					}
				}
				allPersons(first: 10000, excludeClientUsers: true) {
					edges {
						node {
							id
							role {
								id
								name
							}
							email
							firstName
							lastName
							createdAt
							systemUser
							initials
							active
							profilePictureId
							profilePictureDefaultId
							excludeFromCompanyLockedPeriod
							submitLockedDateYear
							submitLockedDateMonth
							submitLockedDateDay
						}
					}
				}
			}
		}
	`,
});
